import { useEffect, useState } from 'react'
import { apiUrl } from '../lib/api'

/**
 * Nákup dárkového voucheru — přes NÁŠ rezervační systém a GoPay bránu.
 * Po zaplacení backend vystaví voucher a pošle SMS s kódem (payments/notify).
 * Návrat z brány: GoPay vrací na /?paid=<payment_id> — pokud id sedí na náš
 * uložený nákup, ukážeme potvrzení.
 */

const PRESETS = [500, 1000, 2000, 5000]

export function VoucherPurchase() {
  const [amount, setAmount] = useState<number>(1000)
  const [custom, setCustom] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [paidOk, setPaidOk] = useState(false)

  useEffect(() => {
    const paid = new URLSearchParams(window.location.search).get('paid')
    const stored = localStorage.getItem('sobacov_voucher_pid')
    if (paid && stored && paid === stored) {
      fetch(apiUrl(`/api/sobacov/payments/${paid}/status`))
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
          if (j?.status === 'paid') {
            setPaidOk(true)
            localStorage.removeItem('sobacov_voucher_pid')
            setTimeout(() => document.getElementById('koupit-voucher')?.scrollIntoView({ behavior: 'smooth' }), 300)
          }
        })
        .catch(() => {})
    }
  }, [])

  const effAmount = custom ? parseInt(custom.replace(/\s/g, ''), 10) || 0 : amount

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    if (effAmount < 300 || effAmount > 20000) {
      setErr('Částka musí být mezi 300 a 20 000 Kč.')
      return
    }
    setBusy(true)
    try {
      const r = await fetch(apiUrl('/api/sobacov/vouchers/gift/public'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_czk: effAmount,
          customer_name: name.trim(),
          customer_phone: phone.trim(),
          customer_email: email.trim() || undefined,
        }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(j.detail || 'Nepodařilo se založit platbu')
      localStorage.setItem('sobacov_voucher_pid', String(j.payment_id))
      window.location.href = j.gw_url
    } catch (e: any) {
      setErr(e.message)
      setBusy(false)
    }
  }

  return (
    <section id="koupit-voucher" className="py-20 md:py-28 bg-white">
      <div className="container-x">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <p className="h-eyebrow">Dárkový voucher</p>
            <h2 className="h-display text-3xl md:text-[44px] mt-4">
              Darujte <span className="h-accent">Sobáčov</span>
            </h2>
            <p className="mt-5 text-ink-700 text-base md:text-lg">
              Voucher v&nbsp;libovolné hodnotě platí na ubytování, rybolov i&nbsp;sport.
              Zaplatíte kartou, kód voucheru přijde okamžitě SMS — stačí ho nadiktovat
              při rezervaci nebo na recepci.
            </p>
            <ul className="mt-6 space-y-2.5">
              {['Platba kartou přes zabezpečenou bránu', 'Kód přijde SMS ihned po zaplacení', 'Platnost 1 rok od nákupu', 'Uplatnění na cokoli v areálu'].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-ink-800">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 mt-1 shrink-0 text-brand-500" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12.5l5 5L20 6.5" />
                  </svg>
                  <span className="text-[15px]">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card p-7 md:p-9">
            {paidOk ? (
              <div className="text-center py-10">
                <svg viewBox="0 0 24 24" className="w-12 h-12 mx-auto mb-3 text-brand-500" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12.5l5 5L20 6.5" />
                </svg>
                <div className="font-bold text-xl text-ink-900">Zaplaceno — děkujeme!</div>
                <p className="text-ink-700 mt-2 text-sm">
                  Kód voucheru vám právě odešel SMS na zadané číslo.
                  Kdyby nedorazil do pár minut, volejte 775&nbsp;140&nbsp;705.
                </p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <h3 className="font-bold text-lg text-ink-900 tracking-tight">Koupit voucher</h3>

                <div className="mt-4">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-ink-700/70 font-semibold">Hodnota</span>
                  <div className="mt-1.5 grid grid-cols-4 gap-2">
                    {PRESETS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => { setAmount(p); setCustom('') }}
                        className={`py-2.5 rounded-lg border text-[14px] font-bold transition-colors ${
                          !custom && amount === p
                            ? 'bg-ink-900 text-white border-ink-900'
                            : 'bg-white text-ink-900 border-ink-900/15 hover:border-ink-900/40'
                        }`}
                      >
                        {p.toLocaleString('cs-CZ')}
                      </button>
                    ))}
                  </div>
                  <input
                    inputMode="numeric"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="…nebo vlastní částka v Kč"
                    className="mt-2 w-full border border-ink-900/15 rounded-lg px-3 py-2 text-ink-800 focus:outline-none focus:border-brand-500"
                  />
                </div>

                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-ink-700/70 font-semibold">Jméno *</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} required
                      className="mt-1.5 w-full border border-ink-900/15 rounded-lg px-3 py-2 text-ink-800 focus:outline-none focus:border-brand-500" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-ink-700/70 font-semibold">Telefon * (přijde SMS s kódem)</span>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} required
                      className="mt-1.5 w-full border border-ink-900/15 rounded-lg px-3 py-2 text-ink-800 focus:outline-none focus:border-brand-500" />
                  </label>
                </div>
                <label className="block mt-4">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-ink-700/70 font-semibold">E-mail (nepovinný)</span>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 w-full border border-ink-900/15 rounded-lg px-3 py-2 text-ink-800 focus:outline-none focus:border-brand-500" />
                </label>

                {err && <div className="mt-3 text-brand-600 text-sm">{err}</div>}

                <button type="submit" disabled={busy || !effAmount} className="btn-book w-full mt-5">
                  {busy ? 'Přesměrovávám na platbu…' : `Zaplatit ${effAmount ? effAmount.toLocaleString('cs-CZ') : '—'} Kč kartou`}
                </button>
                <p className="mt-3 text-[11px] text-ink-700">
                  Platba běží přes zabezpečenou platební bránu. Odesláním souhlasíte se{' '}
                  <a href="#gdpr" className="underline hover:text-brand-600">zpracováním osobních údajů</a>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
